import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { captureUtm } from "../lib/utm";
import { useRokuContactOnCallLinks } from "../lib/rkp";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Phone, Star, CheckCircle } from "@phosphor-icons/react";

const PHONE = "800-719-6912";
const PHONE_TEL = "tel:8007196912";
const GOOGLE_URL = "https://search.google.com/local/writereview?placeid=ChIJezAnVXBVwYkREnwdgohqzGM";

const RELATIONSHIPS = ["Client", "Family member", "Caregiver", "Other"];

function Stars({ n, size = 18 }) {
  return (
    <span className="inline-flex gap-0.5" aria-label={`${n} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={size} weight={i <= n ? "fill" : "regular"}
          className={i <= n ? "text-[#6A3E97]" : "text-stone-300"} />
      ))}
    </span>
  );
}

export default function ReviewsPage() {
  const [reviews, setReviews] = useState(null);
  const [form, setForm] = useState({ name: "", relationship: "", rating: 0, text: "" });
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  useRokuContactOnCallLinks();

  useEffect(() => {
    captureUtm();
    api.get("/public/reviews").then((r) => setReviews(r.data.reviews || [])).catch(() => setReviews([]));
  }, []);

  const valid = (anonymous || form.name.trim()) && form.rating >= 1 && form.text.trim();

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/public/reviews", { ...form, name: anonymous ? "Anonymous" : form.name.trim() });
      setSent(true);
      document.getElementById("write-review")?.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-white text-[#23282f]" data-testid="reviews-page">
      <header className="border-b border-stone-100 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 sm:px-8">
          <a href="/" className="flex items-center gap-2.5">
            <img src="/shc-logo.png" alt="Silver Home Care" className="h-10 w-auto" />
            <p className="font-heading text-lg font-semibold tracking-tight">Silver Home Care</p>
          </a>
          <nav className="hidden items-center gap-6 text-sm font-medium text-[#23282f] md:flex" data-testid="reviews-nav">
            <a href="/#services" className="transition-colors hover:text-[#6A3E97]">Services</a>
            <a href="/#about" className="transition-colors hover:text-[#6A3E97]">About us</a>
            <a href="/#contact" className="transition-colors hover:text-[#6A3E97]">Contact us</a>
            <Link to="/reviews" className="transition-colors hover:text-[#6A3E97]">Reviews</Link>
            <a href="/apply" className="transition-colors hover:text-[#6A3E97]" data-testid="reviews-nav-apply">Caregivers apply</a>
          </nav>
          <a href={PHONE_TEL} className="inline-flex items-center gap-2 rounded-full bg-[#6A3E97] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#4A235A]" data-testid="header-phone">
            <Phone size={16} weight="fill" /> {PHONE}
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 pb-20 pt-14 sm:px-8 sm:pt-20">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6A3E97]">Silver Home Care · Fairless Hills, PA</p>
          <h1 className="mt-5 font-heading text-4xl font-medium leading-[1.15] tracking-tight sm:text-5xl" data-testid="reviews-headline">
            What families say about us.
          </h1>
          <p className="mt-6 text-lg leading-[1.9] text-stone-700">
            Real words from clients, families, and caregivers across Bucks and Philadelphia Counties.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#write-review" className="inline-flex items-center gap-2 rounded-full bg-[#6A3E97] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#4A235A]" data-testid="share-experience-cta">
              Share your experience
            </a>
            <a href={GOOGLE_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-6 py-3 text-sm font-semibold text-stone-700 transition-colors hover:border-[#6A3E97] hover:text-[#6A3E97]" data-testid="google-review-cta">
              Review us on Google
            </a>
          </div>
        </div>

        <div className="mt-16" data-testid="reviews-list">
          {reviews === null ? null : reviews.length === 0 ? (
            <p className="max-w-2xl text-lg leading-[1.9] text-stone-600" data-testid="reviews-empty">
              We're just getting started collecting reviews here. If we've cared for your family,
              yours can be the first — it takes about a minute.
            </p>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {reviews.map((r) => (
                <figure key={r.id} className="rounded-xl border border-stone-200 bg-[#F5F2FA] p-7" data-testid={`review-card-${r.id}`}>
                  <Stars n={r.rating} />
                  <blockquote className="mt-4 text-lg leading-[1.8] text-stone-700">"{r.text}"</blockquote>
                  <figcaption className="mt-4 text-sm font-semibold text-[#23282f]">
                    {r.name}
                    {r.relationship && <span className="ml-2 font-normal text-stone-500">· {r.relationship}</span>}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </div>

        <div id="write-review" className="mt-16 max-w-2xl scroll-mt-24 border-t border-stone-200 pt-10" data-testid="write-review">
          <h2 className="font-heading text-2xl font-medium tracking-tight sm:text-3xl">Share your experience</h2>
          <div className="mt-5 space-y-2.5 text-base leading-relaxed text-stone-600" data-testid="review-guidelines">
            <p>· Use your name or stay anonymous — whatever you're comfortable with.</p>
            <p>· If you're unhappy about anything, we want to know — please call us at{" "}
              <a href={PHONE_TEL} className="font-semibold text-[#6A3E97] hover:underline">{PHONE}</a> so we can make it right.</p>
            <p>· Please don't mention a client by name unless that client is you.</p>
            <p>· By submitting, you're giving us permission to post your words on our website.</p>
          </div>
          {sent ? (
            <div className="mt-6" data-testid="review-success">
              <p className="flex items-start gap-3 text-lg leading-relaxed text-stone-700">
                <CheckCircle size={26} weight="fill" className="mt-1 shrink-0 text-[#6A3E97]" />
                <span>Thank you{form.name ? `, ${form.name.split(" ")[0]}` : ""}. Our office reads
                  every review before it appears on this page.</span>
              </p>
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Input value={anonymous ? "" : form.name} disabled={anonymous}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder={anonymous ? "Anonymous" : "Your name *"} className="h-12 border-stone-300 text-base disabled:bg-stone-100 disabled:text-stone-400" data-testid="review-name" />
                <select value={form.relationship} onChange={(e) => setForm((f) => ({ ...f, relationship: e.target.value }))}
                  className="h-12 rounded-md border border-stone-300 bg-white px-3 text-base text-[#23282f]" data-testid="review-relationship">
                  <option value="">You are a… (optional)</option>
                  {RELATIONSHIPS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2.5 text-sm text-stone-600">
                <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)}
                  className="h-4 w-4 rounded border-stone-300 accent-[#6A3E97]" data-testid="review-anonymous" />
                Keep me anonymous — don't show my name
              </label>
              <div className="flex items-center gap-2" data-testid="review-rating">
                <span className="text-sm text-stone-500">Your rating:</span>
                {[1, 2, 3, 4, 5].map((i) => (
                  <button key={i} type="button" onClick={() => setForm((f) => ({ ...f, rating: i }))}
                    aria-label={`${i} star${i > 1 ? "s" : ""}`} data-testid={`review-star-${i}`}
                    className="rounded p-1 transition-transform hover:scale-110">
                    <Star size={26} weight={i <= form.rating ? "fill" : "regular"}
                      className={i <= form.rating ? "text-[#6A3E97]" : "text-stone-300"} />
                  </button>
                ))}
              </div>
              <textarea value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
                placeholder="A few words about the care… *" rows={4}
                className="w-full rounded-md border border-stone-300 bg-white px-3 py-2.5 text-base text-[#23282f]" data-testid="review-text" />
              <Button onClick={submit} disabled={busy || !valid} size="lg"
                className="rounded-md bg-[#6A3E97] px-8 py-3.5 text-base font-semibold text-white hover:bg-[#4A235A]"
                data-testid="review-submit">
                {busy ? "Sending…" : "Send your review"}
              </Button>
              <p className="text-xs text-stone-400">Reviews are read by our office before they appear on this page.</p>
            </div>
          )}
        </div>
      </main>

      <footer className="bg-[#2E1A3E] py-10 text-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-white/50">
            <p>© {new Date().getFullYear()} Silver Home Care · Licensed Pennsylvania non-medical home care agency · {PHONE}</p>
            <div className="flex items-center gap-5">
              <a href="/" className="font-medium text-white/60 transition-colors hover:text-white" data-testid="footer-main-site">Main site</a>
              <a href="/#contact" className="font-medium text-white/60 transition-colors hover:text-white" data-testid="footer-contact">Contact us</a>
              <a href="/apply" className="font-medium text-white/60 transition-colors hover:text-white" data-testid="footer-careers">Caregivers apply</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
